import { Card, Stack, Typography } from '@mui/material';
import { Accommodation, PerformerRequest, TERMINAL_PERFORMER_REQUEST_STATUSES } from '@silbo/core';
import { ArrowRightIcon, ChevronSmallRightIcon, EllipsisTypography, Emoji, palette } from '@silbo/ui';
import { Link } from '@tanstack/react-router';
import { format, isToday } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getPatientEmojiIcon } from '../../../utils/get-patient-emoji-icon';
import { getRequestReason } from '../utils/get-request-reason';
import { getRequestType } from '../utils/get-request-type';
import RequestStatusChip from './RequestStatusChip';
import RequestTypeIcon from './RequestTypeIcon';

type ArchivedRequestCardProps = {
  request: PerformerRequest;
  accommodation?: Accommodation;
};

const ArchivedRequestCard = ({ request, accommodation }: ArchivedRequestCardProps) => {
  const reason = getRequestReason(request);
  const type = getRequestType(request);
  // Une demande refusée ou re-fléchée est estompée : le service n'a plus rien à en faire
  const terminatedOpacity = TERMINAL_PERFORMER_REQUEST_STATUSES.includes(request.status) ? 0.6 : 1;

  const { t } = useTranslation('bed-management');

  if (!request.request.visit || !type || !request.fulfilledAt) {
    return null;
  }

  return (
    <Link
      to="/bed-management/archives/$id"
      params={{ id: request.id }}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Card elevation={1} sx={{ paddingX: 3, paddingY: 4, cursor: 'pointer', '&:hover': { elevation: 3 } }}>
        <Stack direction="row" alignItems="center" gap={4}>
          <Stack spacing={2} alignItems="center" sx={{ opacity: terminatedOpacity }}>
            <RequestTypeIcon type={type} />
            <Typography variant="body2" color="secondary">
              {format(request.fulfilledAt, 'HH:mm')}
            </Typography>
          </Stack>
          <Stack spacing={2} justifyContent="space-between" flex={1}>
            <Stack
              direction="row"
              alignItems="center"
              gap={2}
              sx={{ opacity: terminatedOpacity }}
            >
              <Emoji
                name={getPatientEmojiIcon(request.request.visit.gender, request.request.visit.age)}
                bgColor={palette.background.default}
              />
              <Typography fontWeight={600}>{request.request.visit.fullName}</Typography>
            </Stack>
            {reason && <Typography sx={{ opacity: terminatedOpacity }}>{reason}</Typography>}
            <Stack direction="row" alignItems="center" gap={2}>
              <RequestStatusChip status={request.status} />
              {request.status === 'ACCEPTED' && accommodation && (
                <EllipsisTypography color="secondary">
                  {t('archive.for')}{' '}
                  {isToday(accommodation.startAt)
                    ? format(accommodation.startAt, 'HH:mm')
                    : format(accommodation.startAt, 'dd/MM HH:mm')}
                </EllipsisTypography>
              )}
            </Stack>
            <Stack direction="row" gap={1} alignItems="center">
              <EllipsisTypography color="secondary" sx={{ opacity: terminatedOpacity }}>
                {request.request.originAccommodation?.unit?.name || request.request.directAdmissionOrigin || '-'}
              </EllipsisTypography>
              <ArrowRightIcon
                sx={{ color: palette.text.secondary, opacity: terminatedOpacity }}
              />
              <EllipsisTypography color="secondary" sx={{ opacity: terminatedOpacity }}>
                {request.performer.name}
              </EllipsisTypography>
            </Stack>
          </Stack>
          <ChevronSmallRightIcon color="secondary" />
        </Stack>
      </Card>
    </Link>
  );
};

export default ArchivedRequestCard;
